import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import Sidebar from "@/components/layout/sidebar";
import TopBar from "@/components/layout/TopBar";
import { BreadcrumbProvider } from "@/components/layout/breadcrumbs";
import { CommandPalette } from "@/components/layout/palette/CommandPalette";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";

interface ShellProps {
  children: React.ReactNode;
}

type Theme = "light" | "dark";

/** Saved choice (`localStorage.theme`) → else the OS preference. Storage may be blocked. */
function initialTheme(): Theme {
  try {
    const saved = localStorage.getItem("theme");
    if (saved === "light" || saved === "dark") return saved;
  } catch {
    // Storage blocked — fall through to the OS preference.
  }
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  } catch {
    return "light";
  }
}

/**
 * The CMS frame: grouped menu (desktop rail / phone drawer), top bar with breadcrumb
 * and theme switch, the Ctrl/⌘ K palette. Page content keeps the legacy `bg-background`.
 */
export default function Shell({ children }: ShellProps) {
  const [location] = useLocation();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const [theme, setTheme] = useState<Theme>(initialTheme);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  // Close the phone drawer on EVERY location change (wouter <Link> uses pushState, not popstate).
  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location]);

  const toggleTheme = () => {
    const next: Theme = theme === "dark" ? "light" : "dark";
    setTheme(next);
    try {
      localStorage.setItem("theme", next);
    } catch {
      // Storage blocked — the choice lasts for this page only.
    }
  };

  const openPalette = () => {
    setIsMobileSidebarOpen(false);
    setIsPaletteOpen(true);
  };

  return (
    <BreadcrumbProvider>
      <div className="flex h-screen bg-background">
        {/* Desktop menu */}
        <div className="hidden h-full lg:block">
          <Sidebar onOpenPalette={openPalette} />
        </div>

        {/* Phone menu */}
        <Sheet open={isMobileSidebarOpen} onOpenChange={setIsMobileSidebarOpen}>
          <SheetContent
            side="left"
            aria-label="Menu"
            aria-describedby={undefined}
            className="w-[280px] border-0 bg-ds-nav p-0 text-ds-navink sm:w-[320px] [&>button]:text-ds-navink"
          >
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <Sidebar variant="drawer" onOpenPalette={openPalette} />
          </SheetContent>
        </Sheet>

        <div className="flex h-full flex-1 flex-col overflow-hidden">
          <TopBar onMenuClick={() => setIsMobileSidebarOpen(true)} theme={theme} onThemeToggle={toggleTheme} />

          <main className="flex-1 overflow-y-auto px-2 py-2 sm:px-4 sm:py-4 lg:px-6 lg:py-6">
            <div className="mx-auto max-w-[1600px]">{children}</div>
          </main>
        </div>

        <CommandPalette open={isPaletteOpen} onOpenChange={setIsPaletteOpen} />
      </div>
    </BreadcrumbProvider>
  );
}
